import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiAuthError } from './lib/api';
import { AppLayout } from './shell';
import type { BreadcrumbItem, SidebarSection, SidebarUser } from './shell';
import { useToast, ImpersonationBanner, ViewAsMenu, CommandBar, NotificationBell, WelcomeTour } from './organisms';
import { Button, SearchTrigger, IconSignOut, IconHelp } from './atoms';
import { identify, groupTenant, resetAnalytics } from './lib/analytics';
import { useBreadcrumbs } from './lib/useBreadcrumbs';
import { usePermissions } from './lib/usePermissions';
import { visibleSections } from './lib/navGating';
import { helpForRoute } from './help/registry';
import { useFlag, FEATURE } from './lib/useFlag';
import { useLawEdition } from './law/useLawEdition';
import type { GatedSection } from './lib/navGating';
import { setActiveOrgTz } from './lib/orgTimezone';
import { ClientPortalApp } from './portal/ClientPortalApp';
import { ConsultantPortalApp } from './portal/ConsultantPortalApp';
import { BrandingProvider, useBranding } from './lib/useBranding';
import type { ReactNode } from 'react';

/**
 * App — root authenticated-layout wrapper.
 *
 * Renders Sidebar + TopBar via AppLayout and an <Outlet /> for the
 * matched child route. Child routes mount inside that outlet — Dashboard
 * (index), future Clients / Properties / Parcels list+detail screens, etc.
 *
 * Auth gating: on 401 from /api/current-user, navigates to /login with
 * a `next` query parameter so the operator returns to where they were
 * trying to go after logging in. (basename="/" — the navigate target is
 * root-relative; FR-6 cutover retired the old /v2 base.)
 *
 * Phase 11 Block 5/6 foundation + Block 7 PR 1 (Outlet + nested routes).
 */

interface CurrentUserResponse {
  id: string;
  email: string;
  role: string;
  organization_id: string | null;
  first_name: string | null;
  last_name: string | null;
  employee_title: string | null;
  org_name: string | null;
  timezone?: string | null;
  // Impersonation state (super_admin "View as"). Present only while this session
  // was rebound to another user by a super_admin; `actor` names the real actor.
  impersonating?: boolean;
  actor?: { id: string; name: string; email: string; role: string } | null;
}

// Roles that may use the "View as" control (mount only for these).
const CAN_VIEW_AS = new Set(['super_admin', 'admin']);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const currentUserQuery = useQuery<CurrentUserResponse>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUserResponse>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  // Name-resolved breadcrumb trail. Called unconditionally (before any early
  // return) so its internal queries obey the rules of hooks.
  const breadcrumbs = useBreadcrumbs(location.pathname);

  // The caller's own permission grid — drives which object nav items render.
  // Called unconditionally (rules of hooks) before the portal early-returns; the
  // portal shells simply don't consume it. Default-DENY: while it loads, gated
  // items stay hidden.
  const permissionsQuery = usePermissions();
  const permissionMap = permissionsQuery.data?.permissions;

  useEffect(() => {
    if (currentUserQuery.error instanceof ApiAuthError) {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [currentUserQuery.error, navigate, location.pathname]);

  // Analytics identity — person = app user id; tenant = org group. Fail-open,
  // no-op when analytics is unkeyed. NO PII in props (email/name omitted); the
  // org name lives on the GROUP only. Runs once per authenticated user load.
  const authedUser = currentUserQuery.data;
  // Publish the org timezone to the module cache the pure date formatters read,
  // so formatDate/formatDateTime render in the org zone everywhere — including
  // column-cell renderers and other non-hook contexts (date-consistency).
  useEffect(() => {
    setActiveOrgTz(authedUser?.timezone ?? undefined);
  }, [authedUser?.timezone]);
  useEffect(() => {
    if (!authedUser?.id) return;
    identify(authedUser.id, { role: authedUser.role });
    if (authedUser.organization_id) {
      groupTenant(authedUser.organization_id, authedUser.org_name ?? undefined);
    }
  }, [authedUser?.id, authedUser?.role, authedUser?.organization_id, authedUser?.org_name]);

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
      resetAnalytics(); // clear identity so the next user isn't merged. Fail-open.
      // Route to the login surface FIRST, then drop cached state: navigating
      // unmounts the authed tree, so clearing can't briefly re-render App with a
      // stale-but-emptied user (which for a client would flash the operator
      // shell / leak staff nav). clear() evicts every client-scoped query so no
      // portal data survives into the next session — the portal login → portal →
      // logout → login round-trip lands on a clean slate.
      navigate('/login', { replace: true });
      queryClient.clear();
      toast({ variant: 'info', message: 'Signed out.' });
    } catch {
      toast({ variant: 'error', message: 'Logout failed' });
    }
  }

  // ⚠️ EVERY HOOK ABOVE EVERY CONDITIONAL RETURN — including the signed-out one below.
  //
  // This hook has been hoisted TWICE, and the second time is the lesson. It first sat below
  // the client/consultant role router, which white-screened every portal user. That fix moved
  // it above the role router — but NOT above the `ApiAuthError` return immediately below, so
  // the identical violation survived on the SIGNED-OUT path: render 1 the query is still
  // loading so we fall through and this hook RUNS; render 2 it rejects, we return null, and it
  // does NOT. React sees fewer hooks, throws #300, and the boundary white-screens a signed-out
  // deep link. Reported against /parcels/new — but the crash is in this shell, not the wizard.
  //
  // Hoisting a hook to just above the return that bit you is not the fix. The rule is
  // absolute: no hook below ANY conditional return.
  const buildModeOn = useFlag(FEATURE.BUILD_MODE);
  const hideParentClients = useFlag(FEATURE.HIDE_PARENT_CLIENTS);
  // AppealStack Law (spec 0009): the matters nav shows only when the firm runs the edition
  // with the roll-up on. A hook, so it sits with the others ABOVE every conditional return.
  const lawMattersOn = useLawEdition().mattersOn;

  if (currentUserQuery.error instanceof ApiAuthError) {
    return null;
  }

  const user = currentUserQuery.data;

  // ─── Impersonation banner (super_admin "View as") ─────────
  // When this session was rebound to another user by a super_admin, a persistent
  // banner rides above EVERY shell (operator, client portal, consultant) with a
  // "return to self" control. Rendered as a fragment sibling so it survives the
  // role router below regardless of which shell renders.
  const currentName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
    : '';
  const impersonationBanner = user?.impersonating && user.actor ? (
    <ImpersonationBanner
      viewingName={currentName}
      viewingRole={user.role}
      actorName={user.actor.name}
    />
  ) : null;
  const withBanner = (content: ReactNode) => (
    <>
      {impersonationBanner}
      {content}
    </>
  );

  // ─── Role router (WS-3.2) ─────────────────────────────────
  // External roles get their own focused portal shell instead of the operator
  // app. Operators (super_admin/admin/administrator/analyst/agent) keep the full
  // AppLayout + Outlet below. While the user is still loading, `role` is undefined
  // and we fall through to the operator render (which tolerates an undefined user).
  const portalUser = user ? {
    name: currentName,
    email: user.email,
    org_name: user.org_name,
  } : undefined;
  if (user?.role === 'client') return withBanner(<ClientPortalApp user={portalUser} onLogout={handleLogout} />);
  // Consultant model decision is still pending — gate defensively: this branch
  // only renders when the backend actually issues role==='consultant' at runtime;
  // otherwise it is dead code and the operator app below is unaffected.
  if (user?.role === 'consultant') return withBanner(<ConsultantPortalApp user={portalUser} onLogout={handleLogout} />);

  const sidebarUser = user ? {
    name: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
    email: user.email,
    role: user.role,
  } : undefined;

  // Each browsable object item declares a `gate` (entity `can_view`, or
  // super_admin-only); aggregate/workflow surfaces (Dashboard, Calendar, Appeals
  // queue, Year Roll) are ungated. The three fee/deal surfaces gate on their own
  // entity — hidden by default (the demo/default seed withholds the grant from
  // non-super roles) yet revealed the moment a super_admin grants it. Org Settings,
  // Audit Log, and User Management are super_admin-only. visibleSections() drops
  // any section left empty after gating. Default-DENY while the grid loads.
  const gatedSections: GatedSection[] = [
          // Section layout mirrors v1 SPA sidebar (public/index.html) — same
          // labels + ordering so operators have zero re-learning at cutover.
          {
            label: 'Workqueue',
            items: [
              { to: '/', label: 'Dashboard', end: true },
              { to: '/tasks', label: 'Tasks' },
              { to: '/calendar', label: 'Calendar' },
              { to: '/appeals/queues', label: 'Appeals' },
            ],
          },
          {
            // Build Mode — the customer's own build loop. Inside the app it is just
            // one menu item, deliberately: a firm owner asks for a feature, and this
            // is where they go. No section header, because a header over a single
            // item reads as a category that has more coming.
            //
            // No ®: "BUILD MODE" is a third party's registration (USPTO Reg. No.
            // 8331829, Class 042) and we hold none, so the symbol would be false
            // marking. The name is authored in four places — here, the /build page
            // heading, the Org Settings section, and the org-settings registry label.
            //
            // TWO gates, answering different questions: the product flag says whether
            // the surface exists here at all (off by default, so Build Mode can sit
            // merged and invisible on the demo), and superAdmin says who may use it once
            // it does. An empty `items` array drops the section entirely rather than
            // leaving a headerless gap.
            items: buildModeOn
              ? [{ to: '/build', label: 'Custom Builds', gate: { superAdmin: true } }]
              : [],
          },
          {
            label: 'Setup',
            items: [
              { to: '/clients/new', label: '+ New Client', gate: { entity: 'client', action: 'can_add' } },
              { to: '/properties/new', label: '+ New Property', gate: { entity: 'property', action: 'can_add' } },
              { to: '/parcels/new', label: '+ New Parcel', gate: { entity: 'parcel', action: 'can_add' } },
            ],
          },
          {
            label: 'Portfolio',
            items: [
              { to: '/clients', label: 'Clients', gate: { entity: 'client' } },
              ...(hideParentClients ? [] : [{ to: '/parent-clients', label: 'Parent Clients', gate: { entity: 'parent_client' } }]),
              { to: '/properties', label: 'Properties', gate: { entity: 'property' } },
              { to: '/parcels', label: 'Parcels', gate: { entity: 'parcel' } },
              // §8.3/§2.5: the standalone Exemption Mass-Update entry is retired —
              // the capability now lives on the jurisdiction profile (apply a cap
              // to every attached parcel).
            ],
          },
          {
            // AppealStack Law — the Litigation section (spec 0009). Appears only when the firm
            // runs the law edition with the matters roll-up on; an empty items array drops the
            // whole section, so a consulting firm sees no trace of it (same pattern as Build Mode).
            label: 'Litigation',
            items: lawMattersOn ? [{ to: '/law/matters', label: 'Matters' }] : [],
          },
          {
            label: 'Tax data',
            items: [
              { to: '/tax-bills', label: 'Tax Bills', gate: { entity: 'tax_bill' } },
              // §8.2: Transmittals is no longer a standalone surface — a transmittal
              // is a tax-bill STATUS ('transmitted'), searchable from the Tax Bills list.
              // §8.1: the standalone Values page is retired for all users —
              // valuation entry now lives inside the parcel profile (ParcelDetail
              // Values tab). Nav entry removed; the route redirects (see main.tsx).
              { to: '/filed-returns', label: 'Returns', gate: { entity: 'filed_return' } },
            ],
          },
          {
            label: 'Government',
            items: [
              { to: '/assessors', label: 'Assessors', gate: { entity: 'assessor' } },
              { to: '/collectors', label: 'Collectors', gate: { entity: 'collector' } },
              { to: '/jurisdictions', label: 'Jurisdictions', gate: { entity: 'jurisdiction' } },
              // Q6+Q7: mass-apply exemption flags / caps / reductions across a parcel selection.
              { to: '/parcels/exemption-proposals', label: 'Mass update', gate: { entity: 'parcel' } },
            ],
          },
          {
            label: 'Billing',
            items: [
              { to: '/invoices', label: 'Invoices', gate: { entity: 'invoice' } },
              // Fee/deal surfaces — super_admin-only by default, grantable via the matrix.
              { to: '/invoice-items', label: 'Invoice Items', gate: { entity: 'invoice_item' } },
              { to: '/invoice-terms', label: 'Invoice Terms', gate: { entity: 'invoice_term' } },
              { to: '/consultant-deals', label: 'Consultant Deals', gate: { entity: 'consultant_deal' } },
              { to: '/commissions', label: 'Commissions', gate: { entity: 'invoice' } },
              { to: '/ar', label: 'Accounts Receivable', gate: { entity: 'invoice' } },
              { to: '/quickbooks', label: 'QuickBooks Sync', gate: { entity: 'invoice' } },
            ],
          },
          {
            label: 'Operations',
            items: [
              { to: '/contacts', label: 'Contacts', gate: { entity: 'contact' } },
              { to: '/groups', label: 'Groups', gate: { entity: 'group' } },
              // `end` so /documents matches EXACTLY — otherwise it prefix-matches
              // /documents/review-queue and stays highlighted while you're on the
              // Review Queue, making that item's click look like it didn't take
              // (the reported "double-click / sticky" nav). Its sibling below owns
              // that path.
              { to: '/documents', label: 'Documents', end: true, gate: { entity: 'document' } },
              { to: '/documents/review-queue', label: 'Document Review Queue', gate: { entity: 'document' } },
              // 7/28 C4: Information Requests sits directly UNDER the Document Review Queue link.
              { to: '/information-requests', label: 'Information Requests', gate: { entity: 'information_request' } },
              { to: '/notifications', label: 'Notifications', gate: { entity: 'notification' } },
              { to: '/activity-log', label: 'Activity Log' },
            ],
          },
          {
            label: 'System',
            items: [
              { to: '/user-management', label: 'User Management', gate: { superAdmin: true } },
              { to: '/org-settings', label: 'Org Settings', gate: { superAdmin: true } },
              { to: '/custom-fields', label: 'Custom Fields', gate: { entity: 'custom_field' } },
              { to: '/state-rules', label: 'State Rules', gate: { entity: 'state_rule' } },
              { to: '/forms', label: 'Forms Library' },
              { to: '/imports', label: 'Import', gate: { entity: 'import_batch' } },
              { to: '/audit-logs', label: 'Audit Log', gate: { superAdmin: true } },
              { to: '/year-roll', label: 'Year Roll' },
            ],
          },
          {
            // Help sits last and is UNGATED — every role can read it, including a
            // brand-new user whose permission grid has not resolved yet. A headerless
            // single item, like Custom Builds above: a "Help" section header over one
            // "Help" link reads as a category with more coming.
            items: [
              { to: '/help', label: 'Help' },
            ],
          },
          {
            // §9: the Reports section is exactly two entries — All Reports and
            // Report Builder. Individual canned reports come off the sidebar and
            // are reached from the All Reports index.
            label: 'Reports',
            items: [
              { to: '/reports', label: 'All Reports', end: true, gate: { entity: 'report' } },
              { to: '/report-builder', label: 'Report Builder', gate: { entity: 'report' } },
            ],
          },
  ];

  // Pass isLoading so gated items show optimistically until the permission grid
  // resolves — a stable nav item set from first paint, so nothing shifts under the
  // cursor mid-click (the "some nav items need a double-click" report). The
  // RouteGuard still enforces the real grant on click.
  const sidebarSections: SidebarSection[] = visibleSections(
    gatedSections,
    permissionMap,
    user?.role,
    permissionsQuery.isLoading,
  );

  // "View as" control — top-bar picker, mounted only for super_admin/admin.
  const viewAsControl = user && CAN_VIEW_AS.has(user.role)
    ? <ViewAsMenu currentUserId={user.id} />
    : null;

  // Branding provider wraps the operator shell so an admin's white-label edits
  // (primary/accent colors + display name) apply app-wide via the dedicated
  // /api/branding surface. Non-blocking: children always render (defaults on error).
  return withBanner(
    <BrandingProvider>
      <OperatorLayout
        sections={sidebarSections}
        sidebarUser={sidebarUser}
        breadcrumbs={breadcrumbs}
        fallbackBrandName={user?.org_name ?? 'AppealStack'}
        onLogout={handleLogout}
        viewAsControl={viewAsControl}
      />
    </BrandingProvider>
  );
}

/**
 * The operator AppLayout, themed by the org's branding. Reads useBranding() (so
 * it must render INSIDE <BrandingProvider>): the sidebar brand name prefers the
 * org's white-label display_name, falling back to the org name / 'AppealStack'.
 */
function OperatorLayout({
  sections,
  sidebarUser,
  breadcrumbs,
  fallbackBrandName,
  onLogout,
  viewAsControl,
}: {
  sections: SidebarSection[];
  sidebarUser: SidebarUser | undefined;
  breadcrumbs: BreadcrumbItem[];
  fallbackBrandName: string;
  onLogout: () => void;
  viewAsControl?: ReactNode;
}): ReactNode {
  const branding = useBranding();
  const brandName = branding.display_name && branding.display_name !== 'AppealStack'
    ? branding.display_name
    : fallbackBrandName;

  return (
    <AppLayout
      sidebar={{
        sections,
        user: sidebarUser,
        brandName,
        brandTagline: 'v2',
      }}
      topbar={{
        breadcrumbs,
        actions: (
          <>
            <SearchTrigger />
            <HelpButton />
            <NotificationBell />
            {viewAsControl}
            <Button variant="ghost" size="sm" onClick={onLogout} aria-label="Sign out" title="Sign out">
              <IconSignOut size={18} />
            </Button>
          </>
        ),
      }}
    >
      {/* Global ⌘K command-bar search — mounted once for the operator shell. */}
      <CommandBar />
      {/* First-login welcome tour. Mounted once; opens itself only for a user who
          has neither completed nor dismissed it, and on demand from the Help Center. */}
      <WelcomeTour />
      <Outlet />
    </AppLayout>
  );
}

/**
 * HelpButton — top-bar entry to the Help Center, aimed at the CURRENT screen.
 *
 * Routes with a mapped article open that article; anything unmapped opens the
 * Help index rather than guessing, because landing somebody on a confidently
 * wrong article is worse than landing them on a search box.
 */
function HelpButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slug = helpForRoute(pathname);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => navigate(slug ? `/help/${slug}` : '/help')}
      data-tour="help-button"
      aria-label="Help for this screen"
      title="Help for this screen"
    >
      <IconHelp size={18} />
    </Button>
  );
}

export default App;
